<template>
  <div>
    <!-- Floating trigger button -->
    <button
      v-if="isLoggedIn && canUseChat"
      class="ai-fab"
      @click="toggleChat"
      :title="isOpen ? 'Close assistant' : 'Open AI Assistant'"
    >
      <v-icon size="26" color="white">
        {{ isOpen ? 'mdi-chevron-down' : 'mdi-robot-outline' }}
      </v-icon>
    </button>

    <!-- Chat panel -->
    <transition name="ai-slide">
      <div v-if="isLoggedIn && canUseChat && isOpen" class="ai-panel">

        <!-- Header -->
        <div class="ai-panel__header">
          <div class="ai-panel__header-left">
            <div class="ai-panel__avatar">
              <v-icon size="20" color="white">mdi-robot-outline</v-icon>
            </div>
            <div>
              <div class="ai-panel__name">AI Assistant</div>
              <div class="ai-panel__status">
                <span class="ai-panel__dot"></span> {{ currentLanguage === 'nl' ? 'Online' : 'Online' }}
              </div>
            </div>
          </div>
          <button @click="clearChat" class="ai-panel__clear">
            <v-icon size="16" color="rgba(255,255,255,0.8)">mdi-trash-can-outline</v-icon>
          </button>
        </div>

        <!-- Messages -->
        <div class="ai-panel__messages" ref="messageList">

          <!-- Welcome state -->
          <div v-if="messages.length === 0" class="ai-welcome">
            <div class="ai-welcome__icon">
              <v-icon size="32" color="#f44943">mdi-robot-outline</v-icon>
            </div>
            <div class="ai-welcome__title">{{ currentLanguage === 'nl' ? 'Hoe kan ik u helpen?' : 'How can I help you?' }}</div>
            <div class="ai-welcome__sub">{{ welcomeSubText }}</div>
            <div class="ai-suggestions">
              <div
                v-for="s in activeSuggestions"
                :key="s"
                class="ai-suggestion"
                @click="useSuggestion(s)"
              >{{ s }}</div>
            </div>
          </div>

          <!-- Messages -->
          <template v-else>
            <div
              v-for="(msg, i) in messages"
              :key="i"
              class="ai-msg"
              :class="msg.role === 'user' ? 'ai-msg--user' : 'ai-msg--bot'"
            >
              <div v-if="msg.role === 'assistant'" class="ai-msg__avatar">
                <v-icon size="14" color="white">mdi-robot-outline</v-icon>
              </div>
              <div class="ai-msg__bubble" v-html="renderMarkdown(msg.content)"></div>
            </div>

            <!-- Typing indicator -->
            <div v-if="loading" class="ai-msg ai-msg--bot">
              <div class="ai-msg__avatar">
                <v-icon size="14" color="white">mdi-robot-outline</v-icon>
              </div>
              <div class="ai-msg__bubble ai-msg__bubble--typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          </template>
        </div>

        <!-- Input (slash menu lives inside for correct absolute positioning) -->
        <div class="ai-panel__input">
          <transition name="slash-fade">
            <div v-if="slashMenuVisible && filteredSlashCommands.length" class="ai-slash-menu">
              <div
                v-for="(cmd, i) in filteredSlashCommands"
                :key="cmd.label"
                class="ai-slash-item"
                :class="{ 'ai-slash-item--active': i === slashIndex }"
                @mousedown.prevent="selectSlashCommand(cmd)"
              >
                <span class="ai-slash-item__slash">/</span>
                <span class="ai-slash-item__label">{{ cmd.label }}</span>
              </div>
            </div>
          </transition>
          <input
            ref="inputField"
            v-model="inputText"
            class="ai-input"
            :placeholder="currentLanguage === 'nl' ? 'Stel een vraag... (typ / voor opdrachten)' : 'Ask a question... (type / for commands)'"
            :disabled="loading"
            @keydown="onInputKeydown"
          />
          <button
            class="ai-send"
            :class="{ 'ai-send--active': inputText.trim() && !loading }"
            :disabled="!inputText.trim() || loading"
            @click="sendMessage"
          >
            <v-icon size="18" :color="inputText.trim() && !loading ? '#f44943' : '#ccc'">
              mdi-send
            </v-icon>
          </button>
        </div>

      </div>
    </transition>
  </div>
</template>

<script>
import axios from "@/apis/axios";

export default {
  name: "AiChatWidget",

  data() {
    return {
      isOpen: false,
      inputText: "",
      loading: false,
      messages: [],
      slashIndex: 0,   // highlighted item in slash menu
      currentLanguage: localStorage.getItem("locale") || "en",
      suggestionsEn: [
        "How many open leads today?",
        "Show leads in Amsterdam",
        "Conversion rate this month",
      ],
      suggestionsNl: [
        "Hoeveel open leads vandaag?",
        "Toon leads in Amsterdam",
        "Conversieratio deze maand",
      ],
    };
  },

  computed: {
    customerMode() {
      // Returns { token, leadId } if on the quotation page with valid params, else null
      if (typeof window === "undefined") return null;
      const path = window.location.pathname;
      if (!path.includes("/quotation-webpage")) return null;
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");
      const leadId = params.get("lead_id") || params.get("id");  // support both URL formats
      if (token && leadId) return { token, leadId };
      return null;
    },

    isLoggedIn() {
      return !!localStorage.getItem("userToken") || !!this.customerMode;
    },

    activeSuggestions() {
      const isNl = this.currentLanguage === 'nl';
      if (this.customerMode) {
        return isNl ? [
          "Wat is de status van mijn dossier?",
          "Heb ik nog openstaande taken?",
          "Kunt u mijn offerte tonen?",
        ] : [
          "What is the status of my case?",
          "Do I have any upcoming tasks?",
          "Can you show me my quotation?",
        ];
      }
      return isNl ? this.suggestionsNl : this.suggestionsEn;
    },

    welcomeSubText() {
      const isNl = this.currentLanguage === 'nl';
      if (this.customerMode) {
        return isNl
          ? "Stel me vragen over uw dossier, offerte of taken."
          : "Ask me about your case, quotation, or tasks.";
      }
      return isNl
        ? "Stel me vragen over leads, partners, taken, offertes of andere CRM-gegevens."
        : "Ask me about leads, partners, tasks, quotations or any CRM data.";
    },

    userGroupCode() {
      if (this.customerMode) return "customer";
      return localStorage.getItem("group") || "";
    },

    canUseChat() {
      if (this.customerMode) return true;
      const allowedGroups = [
        "organization-admin",
        "city-admin",
        "location-admin",
        "advisors",
        "partners",
        "lead-viewers",
        "subsidy-checker",
      ];
      return allowedGroups.includes(this.userGroupCode);
    },

    // All slash commands keyed by group_code
    allSlashCommands() {
      const isNl = this.currentLanguage === 'nl';
      if (isNl) {
        return {
          "organization-admin": [
            { label: "help",                   question: "Wat kunt u mij vragen en in welk formaat?" },
            { label: "open leads",             question: "Hoeveel open leads zijn er in totaal?" },
            { label: "zonder partner",         question: "Toon leads zonder toegewezen partner" },
            { label: "conversieratio",         question: "Wat is de algemene conversieratio?" },
            { label: "leads deze week",        question: "Hoeveel leads zijn er deze week aangemaakt?" },
            { label: "leads deze maand",       question: "Hoeveel leads zijn er deze maand aangemaakt?" },
            { label: "leads per stad",         question: "Toon leads gegroepeerd per stad" },
            { label: "leads per adviseur",     question: "Toon leads gegroepeerd per adviseur" },
            { label: "leads per partner",      question: "Toon leads gegroepeerd per partner" },
            { label: "leads per status",       question: "Toon leads gegroepeerd per status" },
            { label: "top partners",           question: "Welke partners hebben de meeste leads?" },
            { label: "verlopen taken",         question: "Toon alle verlopen openstaande taken" },
            { label: "actieve partners",       question: "Hoeveel actieve en inactieve partners zijn er?" },
            { label: "actieve adviseurs",      question: "Hoeveel actieve en inactieve adviseurs zijn er?" },
            { label: "gewonnen leads",         question: "Hoeveel leads zijn er gewonnen?" },
            { label: "verloren leads",         question: "Hoeveel leads zijn er verloren?" },
          ],
          "city-admin": [
            { label: "help",                   question: "Wat kunt u mij vragen en in welk formaat?" },
            { label: "open leads",             question: "Hoeveel open leads zijn er?" },
            { label: "leads per status",       question: "Toon leads gegroepeerd per status" },
            { label: "conversieratio",         question: "Wat is de conversieratio?" },
            { label: "verlopen taken",         question: "Toon verlopen openstaande taken" },
            { label: "zonder partner",         question: "Toon leads zonder toegewezen partner" },
            { label: "leads deze week",        question: "Hoeveel leads zijn er deze week aangemaakt?" },
            { label: "leads per partner",      question: "Toon leads gegroepeerd per partner" },
            { label: "gewonnen leads",         question: "Hoeveel leads zijn er gewonnen?" },
          ],
          "location-admin": [
            { label: "help",                   question: "Wat kunt u mij vragen en in welk formaat?" },
            { label: "open leads",             question: "Hoeveel open leads zijn er?" },
            { label: "zonder partner",         question: "Toon leads zonder toegewezen partner" },
            { label: "verlopen taken",         question: "Toon verlopen openstaande taken" },
            { label: "leads per status",       question: "Toon leads gegroepeerd per status" },
            { label: "leads deze week",        question: "Hoeveel leads zijn er deze week aangemaakt?" },
            { label: "conversieratio",         question: "Wat is de conversieratio?" },
            { label: "leads per partner",      question: "Toon leads gegroepeerd per partner" },
          ],
          "advisors": [
            { label: "help",                   question: "Wat kunt u mij vragen en in welk formaat?" },
            { label: "mijn open leads",        question: "Hoeveel open leads zijn er aan mij toegewezen?" },
            { label: "mijn verlopen taken",    question: "Toon mijn verlopen openstaande taken" },
            { label: "mijn leads deze week",   question: "Hoeveel leads zijn er deze week voor mij aangemaakt?" },
            { label: "mijn conversieratio",    question: "Wat is mijn conversieratio?" },
            { label: "mijn leads per status",  question: "Toon mijn leads gegroepeerd per status" },
            { label: "mijn gewonnen leads",    question: "Hoeveel van mijn leads zijn gewonnen?" },
          ],
          "partners": [
            { label: "help",                   question: "Wat kunt u mij vragen en in welk formaat?" },
            { label: "mijn open leads",        question: "Hoeveel open leads heb ik?" },
            { label: "mijn leads per status",  question: "Toon mijn leads gegroepeerd per status" },
            { label: "mijn conversieratio",    question: "Wat is mijn conversieratio?" },
            { label: "mijn gewonnen leads",    question: "Hoeveel van mijn leads zijn gewonnen?" },
            { label: "mijn offertes",          question: "Toon offertes voor mijn leads" },
            { label: "klantberichten",         question: "Zijn er ongelezen klantchatberichten op mijn leads?" },
          ],
          "lead-viewers": [
            { label: "help",                   question: "Wat kunt u mij vragen en in welk formaat?" },
            { label: "leads totaal",           question: "Hoeveel leads zijn er in mijn gebied?" },
            { label: "leads per status",       question: "Toon leads gegroepeerd per status" },
            { label: "leads per categorie",    question: "Toon leads gegroepeerd per categorie" },
            { label: "leads per maatregel",    question: "Toon leads gegroepeerd per maatregel" },
            { label: "leads per actie",        question: "Toon leads gegroepeerd per actie" },
            { label: "leads deze maand",       question: "Hoeveel leads zijn er deze maand aangemaakt?" },
            { label: "recente leads",          question: "Toon de meest recente leads in mijn gebied" },
          ],
          "subsidy-checker": [
            { label: "help",                   question: "Wat kunt u mij vragen en in welk formaat?" },
            { label: "openstaande subsidies",  question: "Toon openstaande lokale subsidies" },
            { label: "subsidies per status",   question: "Toon lokale subsidies gegroepeerd per status" },
            { label: "goedgekeurde subsidies", question: "Hoeveel lokale subsidies zijn goedgekeurd?" },
            { label: "subsidies per actie",    question: "Toon lokale subsidies gegroepeerd per actie" },
            { label: "subsidies per maatregel",question: "Toon lokale subsidies gegroepeerd per maatregel" },
          ],
          "customer": [
            { label: "help",                   question: "Wat kunt u mij vragen en in welk formaat?" },
            { label: "mijn status",            question: "Wat is de huidige status van mijn dossier?" },
            { label: "statusoverzicht",        question: "Kunt u mijn volledige statusoverzicht tonen en waar ik in het proces ben?" },
            { label: "mijn offerte",           question: "Kunt u mijn laatste offertedetails tonen?" },
            { label: "adviseur contact",       question: "Kunt u de contactgegevens van mijn adviseur en partner geven?" },
          ],
        };
      }
      return {
        "organization-admin": [
          { label: "help",                   question: "What can I ask you and in which format?" },
          { label: "open leads",             question: "How many open leads are there in total?" },
          { label: "partner not assigned",   question: "Show me leads without a partner assigned" },
          { label: "conversion rate",        question: "What is the overall conversion rate?" },
          { label: "leads this week",        question: "How many leads were created this week?" },
          { label: "leads this month",       question: "How many leads were created this month?" },
          { label: "leads by city",          question: "Show me leads grouped by city" },
          { label: "leads by advisor",       question: "Show me leads grouped by advisor" },
          { label: "leads by partner",       question: "Show me leads grouped by partner" },
          { label: "leads by status",        question: "Show me leads grouped by status" },
          { label: "top partners",           question: "Which partners have the most leads?" },
          { label: "overdue tasks",          question: "Show me all overdue open tasks" },
          { label: "active partners",        question: "How many active and inactive partners are there?" },
          { label: "active advisors",        question: "How many active and inactive advisors are there?" },
          { label: "won leads",              question: "How many leads have been won?" },
          { label: "lost leads",             question: "How many leads have been lost?" },
        ],
        "city-admin": [
          { label: "help",                   question: "What can I ask you and in which format?" },
          { label: "open leads",             question: "How many open leads are there?" },
          { label: "leads by status",        question: "Show me leads grouped by status" },
          { label: "conversion rate",        question: "What is the conversion rate?" },
          { label: "overdue tasks",          question: "Show me overdue open tasks" },
          { label: "partner not assigned",   question: "Show me leads without a partner assigned" },
          { label: "leads this week",        question: "How many leads were created this week?" },
          { label: "leads by partner",       question: "Show me leads grouped by partner" },
          { label: "won leads",              question: "How many leads have been won?" },
        ],
        "location-admin": [
          { label: "help",                   question: "What can I ask you and in which format?" },
          { label: "open leads",             question: "How many open leads are there?" },
          { label: "partner not assigned",   question: "Show me leads without a partner assigned" },
          { label: "overdue tasks",          question: "Show me overdue open tasks" },
          { label: "leads by status",        question: "Show me leads grouped by status" },
          { label: "leads this week",        question: "How many leads were created this week?" },
          { label: "conversion rate",        question: "What is the conversion rate?" },
          { label: "leads by partner",       question: "Show me leads grouped by partner" },
        ],
        "advisors": [
          { label: "help",                   question: "What can I ask you and in which format?" },
          { label: "my open leads",          question: "How many open leads are assigned to me?" },
          { label: "my overdue tasks",       question: "Show me my overdue open tasks" },
          { label: "my leads this week",     question: "How many leads were created this week for me?" },
          { label: "my conversion rate",     question: "What is my conversion rate?" },
          { label: "my leads by status",     question: "Show me my leads grouped by status" },
          { label: "my won leads",           question: "How many of my leads have been won?" },
        ],
        "partners": [
          { label: "help",                   question: "What can I ask you and in which format?" },
          { label: "my open leads",          question: "How many open leads do I have?" },
          { label: "my leads by status",     question: "Show me my leads grouped by status" },
          { label: "my conversion rate",     question: "What is my conversion rate?" },
          { label: "my won leads",           question: "How many of my leads have been won?" },
          { label: "my quotations",          question: "Show me quotations for my leads" },
          { label: "customer messages",      question: "Are there any unread customer chat messages on my leads?" },
        ],
        "lead-viewers": [
          { label: "help",                   question: "What can I ask you and in which format?" },
          { label: "total leads",            question: "How many leads are there in my area?" },
          { label: "leads by status",        question: "Show me leads grouped by status" },
          { label: "leads by category",      question: "Show me leads grouped by category" },
          { label: "leads by measurement",   question: "Show me leads grouped by measure/product" },
          { label: "leads by action",        question: "Show me leads grouped by action" },
          { label: "leads this month",       question: "How many leads were created this month?" },
          { label: "recent leads",           question: "Show me the most recent leads in my area" },
        ],
        "subsidy-checker": [
          { label: "help",                   question: "What can I ask you and in which format?" },
          { label: "pending subsidies",      question: "Show me pending local subsidies" },
          { label: "subsidies by status",    question: "Show me local subsidies grouped by status" },
          { label: "approved subsidies",     question: "How many local subsidies have been approved?" },
          { label: "subsidies by action",    question: "Show me local subsidies grouped by action" },
          { label: "subsidies by measure",   question: "Show me local subsidies grouped by measure/product" },
        ],
        "customer": [
          { label: "help",                   question: "What can I ask you and in which format?" },
          { label: "my status",              question: "What is the current status of my case?" },
          { label: "status journey",         question: "Can you show me my full status journey and where I am in the process?" },
          { label: "my quotation",           question: "Can you show me my latest quotation details?" },
          { label: "contact advisor",        question: "Can you give me the contact details of my advisor and partner?" },
        ],
      };
    },

    currentSlashCommands() {
      return this.allSlashCommands[this.userGroupCode] || this.allSlashCommands["location-admin"];
    },

    slashMenuVisible() {
      return this.inputText.startsWith("/") && !this.loading;
    },

    filteredSlashCommands() {
      if (!this.slashMenuVisible) return [];
      const query = this.inputText.slice(1).trim().toLowerCase();
      if (!query) return this.currentSlashCommands;
      return this.currentSlashCommands.filter(c =>
        c.label.toLowerCase().includes(query)
      );
    },
  },

  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.currentLanguage = localStorage.getItem("locale") || "en";
        this.messages = [];
        this.$nextTick(() => {
          this.scrollToBottom();
          this.$refs.inputField && this.$refs.inputField.focus();
        });
      }
    },

    clearChat() {
      this.messages = [];
    },

    useSuggestion(text) {
      this.inputText = text;
      this.sendMessage();
    },

    selectSlashCommand(cmd) {
      this.inputText = cmd.question;
      this.slashIndex = 0;
      this.$nextTick(() => this.$refs.inputField && this.$refs.inputField.focus());
    },

    onInputKeydown(e) {
      if (!this.slashMenuVisible || this.filteredSlashCommands.length === 0) {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          this.sendMessage();
        }
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.slashIndex = (this.slashIndex + 1) % this.filteredSlashCommands.length;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.slashIndex = (this.slashIndex - 1 + this.filteredSlashCommands.length) % this.filteredSlashCommands.length;
      } else if (e.key === "Enter") {
        e.preventDefault();
        const cmd = this.filteredSlashCommands[this.slashIndex];
        if (cmd) this.selectSlashCommand(cmd);
      } else if (e.key === "Escape") {
        this.inputText = "";
        this.slashIndex = 0;
      }
    },

    getCurrentPageContext() {
      const route = this.$route;
      if (!route) return null;
      const path = route.path;
      // Routes use different param names — collect whichever is present
      const numId = parseInt(route.params.id || route.params.energyId || route.params.orderId, 10) || null;

      // --- Detail pages (with numeric ID) ---
      if (path.startsWith("/lead-detail/") && numId)
        return { page: "lead", id: numId };
      if (path.startsWith("/partner-detail/") && numId)
        return { page: "partner", id: numId };
      if (path.startsWith("/task-details/") && numId)
        return { page: "task", id: numId };
      if (path.startsWith("/local-subsidy-detail/") && numId)
        return { page: "local_subsidy", id: numId };
      if (path.startsWith("/user-detail/") && numId)
        return { page: "advisor", id: numId };
      if (path.startsWith("/energy-report-detail/") && numId)
        return { page: "energy_report", id: numId };
      if (path.startsWith("/order-detail/") && numId)
        return { page: "order", id: numId };

      // --- Location detail pages (all sub-pages of a location share the same id) ---
      if (path.startsWith("/overview/") && numId)
        return { page: "location", id: numId };
      if (path.startsWith("/location-partners/") && numId)
        return { page: "location", id: numId };
      if (path.startsWith("/location-cities/") && numId)
        return { page: "location", id: numId };
      if (path.startsWith("/location-employees/") && numId)
        return { page: "location", id: numId };

      // --- House passport (hash-based, no numeric ID) ---
      if (path.startsWith("/house-passport/"))
        return { page: "house_passport", hash: route.params.hash || null };

      // --- Listing pages ---
      if (path === "/leads" || path === "/archive-leads")
        return { page: "leads", archived: path.includes("archive") };
      if (path === "/users" || path === "/archive-users")
        return { page: "partners", archived: path.includes("archive") };
      if (path === "/contact")
        return { page: "contacts" };
      if (path === "/tasks" || path === "/archived-tasks")
        return { page: "tasks", archived: path.includes("archive") };
      if (path === "/subsidy" || path === "/archive-subsidy")
        return { page: "subsidies", archived: path.includes("archive") };
      if (path === "/local-subsidy" || path === "/archive-local-subsidy")
        return { page: "local_subsidies", archived: path.includes("archive") };
      if (path === "/locations")
        return { page: "locations" };
      if (path === "/cities")
        return { page: "cities" };
      if (path === "/advisors")
        return { page: "advisors" };

      // --- Dashboard pages ---
      if (path === "/city-overview" || path === "/city-dashboard")
        return { page: "city_dashboard" };
      if (path === "/partner-dashboard")
        return { page: "partner_dashboard" };
      if (path === "/advisor-dashboard")
        return { page: "advisor_dashboard" };
      if (path === "/local-subsidy-dashboard")
        return { page: "local_subsidy_dashboard" };

      return null;
    },

    async sendMessage() {
      const question = this.inputText.trim();
      if (!question || this.loading) return;

      this.inputText = "";
      this.messages.push({ role: "user", content: question });
      this.loading = true;
      this.$nextTick(() => this.scrollToBottom());

      try {
        // Send full conversation history so OpenAI has context
        const history = this.messages.map((m) => ({
          role: m.role,
          content: m.content,
        }));

        let response;

        if (this.customerMode) {
          // Customer mode: no auth header, send token + lead_id in the body
          response = await axios.post(
            "/api/ai/customer-chat/",
            {
              question,
              history,
              language: this.currentLanguage,
              token: this.customerMode.token,
              lead_id: this.customerMode.leadId,
            },
            { headers: { Authorization: undefined } }
          );
        } else {
          // Employee mode: existing behaviour unchanged
          const pageContext = this.getCurrentPageContext();
          response = await axios.post("/api/ai/chat/", { question, history, language: this.currentLanguage, page_context: pageContext });
        }

        const isNl = this.currentLanguage === 'nl';
        if (response.status === 200 && response.data.answer) {
          this.messages.push({ role: "assistant", content: response.data.answer });
        } else if (response.status === 401) {
          this.messages.push({ role: "assistant", content: isNl ? "Uw sessie is verlopen. Log opnieuw in." : "Your session has expired. Please log in again." });
        } else if (response.status === 429) {
          const msg429 = response.data?.error || (isNl ? "U verstuurt berichten te snel. Wacht even voor u het opnieuw probeert." : "You are sending messages too quickly. Please wait a moment before trying again.");
          this.messages.push({ role: "assistant", content: msg429 });
        } else {
          this.messages.push({ role: "assistant", content: isNl ? "Er is iets misgegaan. Probeer het opnieuw." : "Something went wrong. Please try again." });
        }
      } catch (err) {
        const isNl = this.currentLanguage === 'nl';
        const errMsg =
          err?.response?.data?.error ||
          (err?.response?.status === 429 ? (isNl ? "U verstuurt berichten te snel. Wacht even voor u het opnieuw probeert." : "You are sending messages too quickly. Please wait a moment before trying again.") : null) ||
          (isNl ? "De AI-service is niet bereikbaar. Probeer het opnieuw." : "Could not reach the AI service. Please try again.");
        this.messages.push({ role: "assistant", content: errMsg });
      } finally {
        this.loading = false;
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    renderMarkdown(text) {
      if (!text) return "";
      // Extract markdown links before escaping HTML
      // Pattern: [label](url) — save as placeholders
      const links = [];
      let processed = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) => {
        // Only allow relative paths or same-origin absolute URLs
        const safeUrl = url.startsWith("/") || url.startsWith(window.location.origin)
          ? url
          : "#";
        const idx = links.length;
        links.push(`<a href="${safeUrl}" target="_blank" rel="noopener" class="ai-link">${label}</a>`);
        return `\x00LINK${idx}\x00`;
      });

      processed = processed
        // Escape HTML to prevent XSS
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        // **bold**
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        // *italic*
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        // newlines → <br>
        .replace(/\n/g, "<br>");

      // Restore links
      links.forEach((linkHtml, idx) => {
        processed = processed.replace(`\x00LINK${idx}\x00`, linkHtml);
      });

      return processed;
    },

    scrollToBottom() {
      const el = this.$refs.messageList;
      if (el) el.scrollTop = el.scrollHeight;
    },
  },

  watch: {
    filteredSlashCommands() {
      this.slashIndex = 0;
    },
  },
};
</script>

<style lang="scss" scoped>

// ---------------------------------------------------------------------------
// Floating button
// ---------------------------------------------------------------------------

.ai-fab {
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 1000;
  width: 56px;
  height: 56px;

  @media (max-width: 600px) {
    bottom: 16px;
    right: 16px;
  }
  border-radius: 50%;
  background: linear-gradient(135deg, #f44943 0%, #ff416c 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(244, 73, 67, 0.45);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  outline: none;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(244, 73, 67, 0.55);
  }

  &:active {
    transform: scale(0.96);
  }
}

// ---------------------------------------------------------------------------
// Panel slide transition
// ---------------------------------------------------------------------------

.ai-slide-enter-active,
.ai-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.ai-slide-enter,
.ai-slide-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

.ai-panel {
  position: fixed;
  bottom: 96px;
  right: 28px;
  width: 370px;
  height: 560px;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  overflow: hidden;
  z-index: 999;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.1);
  background: #ffffff;

  @media (max-width: 600px) {
    width: calc(100vw - 16px);
    right: 8px;
    left: 8px;
    bottom: 80px;
    height: calc(100vh - 100px);
    max-height: calc(100vh - 100px);
    border-radius: 16px;
  }
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

.ai-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #f44943 0%, #ff416c 100%);
  flex-shrink: 0;

  &-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.ai-panel__avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ai-panel__name {
  color: white;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.2;
}

.ai-panel__status {
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 1px;
}

.ai-panel__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #a8ff78;
  display: inline-block;
}

.ai-panel__clear {
  opacity: 0.7;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s, background 0.15s;
  outline: none;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.2);
  }
}

// ---------------------------------------------------------------------------
// Messages area
// ---------------------------------------------------------------------------

.ai-panel__messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f7f8fc;
  min-height: 0; // required for flex overflow to work correctly

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: #ddd; border-radius: 4px; }
}

// ---------------------------------------------------------------------------
// Welcome state
// ---------------------------------------------------------------------------

.ai-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 8px 8px;
  text-align: center;

  &__icon {
    width: 56px;
    height: 56px;
    background: rgba(244, 73, 67, 0.08);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #222;
    margin-bottom: 6px;
  }

  &__sub {
    font-size: 12px;
    color: #888;
    line-height: 1.5;
    margin-bottom: 16px;
  }
}

.ai-suggestions {
  display: flex;
  flex-direction: column;
  gap: 7px;
  width: 100%;
}

.ai-suggestion {
  padding: 9px 14px;
  border-radius: 10px;
  border: 1.5px solid rgba(244, 73, 67, 0.2);
  background: white;
  color: #f44943;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(244, 73, 67, 0.06);
    border-color: #f44943;
  }
}

// ---------------------------------------------------------------------------
// Message bubbles
// ---------------------------------------------------------------------------

.ai-msg {
  display: flex;
  align-items: flex-end;
  gap: 7px;

  &--user {
    flex-direction: row-reverse;
  }

  &__avatar {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f44943, #ff416c);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-bottom: 2px;
  }

  &__bubble {
    max-width: 76%;
    padding: 10px 14px;
    font-size: 13px;
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-word;

    .ai-msg--user & {
      background: linear-gradient(135deg, #f44943, #ff416c);
      color: white;
      border-radius: 18px 18px 4px 18px;
    }

    .ai-msg--bot & {
      background: white;
      color: #2d2d2d;
      border-radius: 18px 18px 18px 4px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
    }

    // Typing dots
    &--typing {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 14px 18px;

      span {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #ccc;
        animation: ai-bounce 1.2s infinite ease-in-out;

        &:nth-child(2) { animation-delay: 0.2s; }
        &:nth-child(3) { animation-delay: 0.4s; }
      }
    }
  }
}

@keyframes ai-bounce {
  0%, 80%, 100% { transform: scale(0.65); opacity: 0.4; }
  40%            { transform: scale(1);    opacity: 1; }
}

// ---------------------------------------------------------------------------
// Input area
// ---------------------------------------------------------------------------

.ai-panel__input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: white;
  border-top: 1px solid #eef0f5;
  flex-shrink: 0;
  position: relative;
}

.ai-input {
  flex: 1;
  border: 1.5px solid #e8eaf0;
  border-radius: 24px;
  padding: 9px 16px;
  font-size: 13px;
  outline: none;
  background: #f7f8fc;
  color: #333;
  transition: border-color 0.15s;

  &::placeholder { color: #aaa; }

  &:focus {
    border-color: #f44943;
    background: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.ai-send {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;

  &--active:hover {
    background: rgba(244, 73, 67, 0.08);
  }

  &:disabled { cursor: not-allowed; }
}

.ai-link {
  color: #f44943;
  text-decoration: underline;
  font-weight: 500;
  word-break: break-all;

  &:hover {
    color: #d43530;
  }
}

// ---------------------------------------------------------------------------
// Slash command menu
// ---------------------------------------------------------------------------

.ai-slash-menu {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1.5px solid #eef0f5;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);
  max-height: 220px;
  overflow-y: auto;
  z-index: 10;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: #ddd; border-radius: 4px; }
}

.ai-slash-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.1s;

  &:hover,
  &--active {
    background: rgba(244, 73, 67, 0.06);
  }

  &__slash {
    color: #f44943;
    font-weight: 700;
    flex-shrink: 0;
  }

  &__label {
    color: #333;
  }
}

.slash-fade-enter-active,
.slash-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.slash-fade-enter,
.slash-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
